import './ViewAll.css';
import '../ProjectView/ProjectView.css';
import { ActionButton, Checkbox, CommandBar, DefaultButton, Dialog, DialogFooter, DirectionalHint, Icon, Label, Link, MessageBar, MessageBarType, Modal, PrimaryButton, Spinner, SpinnerSize, Stack, TeachingBubble, TextField } from '@fluentui/react';
import { Component } from 'react';
import * as api from '../../api';
import { CargoGrid, CargoRemaining, ChartGeneralProgress, ProjectLink } from '../../components';
import { appTheme, cn } from '../../theme';
import { autoUpdateFrequency, autoUpdateStopDuration, Cargo, CmdrShip, fc_loading, KnownFC, Project } from '../../types';
import { store } from '../../local-storage';
import { asGrey, delayFocus, fcFullName, getCargoCountOnHand, mergeCargo, openDiscordLink, sumCargo, sumCargo as sumCargos } from '../../util';
import { FleetCarrier } from '../FleetCarrier';
import { CopyButton } from '../../components/CopyButton';
import { ModalCommander } from '../../components/ModalCommander';
import { HaulSize } from '../../components/BigSiteTable/BigSiteTable';
import { redirectToFrontierAuth } from '../../api/auth';
import { renderQuote } from '../Home';
import { ShowCoachingMarks } from '../../components/ShowCoachingMarks';
import { App } from '../../App';

interface ViewAllProps {
}

interface ViewAllState {
  errorMsg?: string;
  loading?: boolean;
  allProjects: Project[],
  projects: Project[],
  linkedFC: KnownFC[],
  hiddenFC: Set<number>,
  hiddenIDs: Set<string>,
  originalHiddenIDs: string,

  /** Merged cargo needs from all projects */
  sumCargo: Cargo,
  /** Merged cargo from all Fleet Carriers */
  fcCargo: Cargo,
  commodityNeeds?: Record<string, Record<string, Record<string, number>>>,
  ships?: CmdrShip[];

  autoUpdateUntil: number;
  lastPoll?: string;
  fcEditMarketId?: string;
  cmdrEdit?: boolean;
  hideLoginPrompt?: boolean;

  showPreNewFcLoading?: boolean;
  newFcLoadingName?: string;
}

export class ViewAll extends Component<ViewAllProps, ViewAllState> {
  private timer?: NodeJS.Timeout;

  constructor(props: ViewAllProps) {
    super(props);

    this.state = {
      allProjects: [],
      projects: [],
      linkedFC: [],
      hiddenFC: new Set<number>(store.viewAllHiddenFC),
      hiddenIDs: new Set<string>(),
      originalHiddenIDs: '',
      sumCargo: {},
      fcCargo: {},
      autoUpdateUntil: 0,
      lastPoll: 'x',
    };
  }

  componentDidMount(): void {
    window.document.title = `Build: ${store.cmdrName}`;

    this.toggleAutoRefresh();
  }

  componentWillUnmount(): void {
    if (this.timer) {
      clearTimeout(this.timer);
    }
  }

  async fetchAll() {
    if (!store.cmdrName) {
      console.warn('You need to sign in in for this');
      return;
    }
    this.setState({ loading: true });

    try {
      // get data from combined API
      const viewAll = await api.cmdr.getViewAll();
      const { projects: allProjects, linkedFC, hiddenIDs } = viewAll;

      // process received data
      const projects = allProjects.filter(p => !hiddenIDs.includes(p.buildId));
      const newSumCargo = mergeCargo(projects.map(p => p.commodities));
      const newCommodityNeeds = this.generateCommodityNeeds(newSumCargo, projects);
      const visibleFC = linkedFC.filter(fc => !this.state.hiddenFC.has(fc.marketId));
      const newBuildIds = projects.map(p => p.buildId);

      this.setState({
        loading: newBuildIds.length !== 0,
        allProjects, projects, sumCargo: newSumCargo,
        commodityNeeds: newCommodityNeeds,
        linkedFC: linkedFC,
        fcCargo: mergeCargo(visibleFC.map(fc => fc.cargo)),
        hiddenIDs: new Set(hiddenIDs),
        originalHiddenIDs: hiddenIDs.sort().join(),
      });
      // remove any stray FC marketIDs
      const marketIDlinkedFC = linkedFC.map(fc => fc.marketId);
      store.viewAllHiddenFC = store.viewAllHiddenFC.filter(mid => marketIDlinkedFC.includes(mid));

      // now start loading ships
      if (newBuildIds) {
        const moreShips = await api.project.getShips(newBuildIds);
        this.setState({ loading: false, ships: moreShips });
      }
    } catch (err: any) {
      console.error(`Error loading data: ${err.message}`);
      this.setState({ loading: false, errorMsg: err.message });
    }
  }

  generateCommodityNeeds(newSumCargo: Cargo, projects: Project[]) {
    const newCommodityNeeds = Object.keys(newSumCargo)
      .reduce((m, key) => {
        const sysSet: Record<string, Record<string, number>> = {};
        for (const p of projects) {
          if (key in p.commodities && p.commodities[key] > 0) {
            if (!sysSet[p.systemName]) { sysSet[p.systemName] = {}; }
            sysSet[p.systemName][p.buildName] = p.commodities[key];
          }
        }
        m[key] = sysSet;
        return m;
      }, {} as Record<string, Record<string, Record<string, number>>>);
    return newCommodityNeeds;
  }

  async fetchProject(buildId: string) {
    this.setState({ loading: true });

    try {
      // get one project
      const newProject = await api.project.get(buildId);
      const newProjects = this.state.projects.map(p => p.buildId === buildId ? newProject : p);
      const newSumCargo = mergeCargo(newProjects.map(p => p.commodities));
      const newCommodityNeeds = this.generateCommodityNeeds(newSumCargo, newProjects);

      this.setState({
        projects: newProjects,
        sumCargo: newSumCargo,
        commodityNeeds: newCommodityNeeds,
      });

      // add artificial delay to avoid flicker on the spinner
      setTimeout(() => this.setState({ loading: false }), 500);
    } catch (err: any) {
      console.error(`Error loading data: ${err.message}`);
      this.setState({ loading: false, errorMsg: err.message });
    }
  }

  saveHiddenIDs = () => {
    this.setState({ loading: true });
    api.cmdr.setHiddenIDs(store.cmdrName, Array.from(this.state.hiddenIDs))
      .then(newHiddenIDs => {
        this.setState({ loading: false, hiddenIDs: new Set(newHiddenIDs), originalHiddenIDs: newHiddenIDs.sort().join() });
      })
      .catch((err: any) => {
        console.error(`saveHiddenIDs failed: ${err.stack}`);
        this.setState({ loading: false, errorMsg: err.message });
      });
  }

  toggleAutoRefresh = () => {
    if (this.state.autoUpdateUntil > 0) {
      // stop auto-refresh poll and don't refresh at this time.
      console.log(`Stopping timer at: ${new Date().toISOString()}`);
      clearTimeout(this.timer);
      this.setState({ autoUpdateUntil: 0 });
    } else {
      // start polling (which causes an immediate refresh)
      console.log(`Starting timer at: ${new Date().toISOString()}`);
      this.setState({ autoUpdateUntil: Date.now() + autoUpdateStopDuration });
      this.pollLastTimestamp(true);
    }
  };

  async pollLastTimestamp(force: boolean = false) {
    try {
      this.setState({ loading: true });

      // call server to see if anything changed
      const { linkedFC, hiddenFC, lastPoll } = this.state;

      let currentPoll = 'x';
      if (!force) {
        var fcIDs = linkedFC
          .filter(fc => !hiddenFC.has(fc.marketId))
          .map(fc => fc.marketId.toString());

        const buildIds = this.state.projects.map(p => p.buildId);
        const pollData = await api.project.poll([...buildIds, ...fcIDs]);
        currentPoll = pollData.max;
      }

      console.log(`pollTimestamp: ${currentPoll} vs ${lastPoll} (match: ${currentPoll === lastPoll}), forced: ${force}`);

      if (currentPoll !== lastPoll || force) {
        this.setState({ lastPoll: currentPoll });
        // something changed
        await this.fetchAll();
      }

      // schedule next poll?
      if (this.state.autoUpdateUntil > 0) {
        if (Date.now() < this.state.autoUpdateUntil) {
          this.timer = setTimeout(() => {
            this.pollLastTimestamp();
          }, autoUpdateFrequency);
        } else {
          console.log(`Stopping auto-update after one hour of no changes at: ${new Date().toISOString()}`);
          this.setState({ autoUpdateUntil: 0 });
        }
      }
    } catch (err: any) {
      console.error(`Stop auto-updating at: ${new Date()} due to:\n`, err?.message);
      this.setState({ autoUpdateUntil: 0 });
    } finally {
      // add artificial delay to avoid flicker on the spinner
      setTimeout(() => this.setState({ loading: false }), 500);
    }
  }

  render() {
    const { errorMsg, autoUpdateUntil, loading, fcEditMarketId, sumCargo, fcCargo, cmdrEdit, hideLoginPrompt, projects, allProjects, commodityNeeds, linkedFC, hiddenFC, ships, showPreNewFcLoading } = this.state;

    const cargoRemaining = sumCargos(sumCargo);
    const cargoOnHand = getCargoCountOnHand(sumCargo, fcCargo)
    const fcRemaining = cargoRemaining - cargoOnHand;

    if (!store.cmdrName) {
      return <div className='contain-vert' style={{ minWidth: 800 }}>
        {renderQuote()}

        <div className='home-box'>
          <DefaultButton iconProps={{ iconName: 'SignIn' }} text='Login' onClick={() => redirectToFrontierAuth()} /> to see your projects.
        </div>

        {!hideLoginPrompt && <TeachingBubble
          target={'#current-cmdr'}
          headline='Greetings'
          calloutProps={{
            preventDismissOnResize: true,
            directionalHint: DirectionalHint.bottomLeftEdge,
          }}
          onDismiss={() => this.setState({ hideLoginPrompt: true })}
          primaryButtonProps={{
            text: 'Okay',
            onClick: () => {
              document.getElementById('current-cmdr')?.click();
              this.setState({ hideLoginPrompt: true });
            }
          }}
          secondaryButtonProps={{
            text: 'Maybe later',
            onClick: () => this.setState({ hideLoginPrompt: true }),
          }}
        >
          You must login through Frontier to save and fully use this tool.
        </TeachingBubble>}
      </div>;
    }

    const visibleFC = linkedFC.filter(fc => !hiddenFC.has(fc.marketId));

    const whereToBuy = !projects?.length
      ? undefined
      : { refSystem: projects[0].systemName, buildIds: allProjects.map(p => p.buildId) }

    return <div className='view-all'>
      <div>
        {errorMsg && <MessageBar messageBarType={MessageBarType.error}>{errorMsg}</MessageBar>}

        <h2 className='project-title'>
          All projects for: {store.cmdrName}
        </h2>

        <CommandBar
          className={`top-bar ${cn.bb} ${cn.bt} ${cn.topBar}`}
          items={[
            {
              key: 'btn-refresh',
              text: 'Refresh',
              title: !!autoUpdateUntil ? 'Click to stop auto updating' : 'Click to refresh now and auto update every 30 seconds',
              iconProps: { iconName: !!autoUpdateUntil ? 'PlaybackRate1x' : 'Refresh' },
              disabled: loading,
              className: cn.bBox,
              style: { color: asGrey(loading) },
              onClick: () => {
                this.toggleAutoRefresh();
              }
            },

            {
              key: 'btn-add',
              text: 'New FC Loading',
              title: 'Create a new FC loading project',
              iconProps: { iconName: 'AddToShoppingList' },
              disabled: !store.apiKey,
              className: cn.bBox,
              onClick: () => {
                this.setState({ showPreNewFcLoading: true, newFcLoadingName: '' });
                delayFocus('#new-loading-name');
              }
            },

            {
              id: 'btn-nexus',
              key: 'btn-nexus',
              text: 'Nexus',
              title: 'View nexus plans',
              iconProps: { iconName: 'BuildQueue' },
              disabled: !store.apiKey,
              className: cn.bBox,
              href: '/#nexus'
            },

            {
              key: 'sys-loading',
              title: 'Networking ...',
              iconProps: { iconName: 'Nav2DMapView' },
              onRender: () => {
                return !loading ? null : <div>
                  <Spinner
                    size={SpinnerSize.medium}
                    labelPosition='right'
                    label='Loading ...'
                    style={{ marginTop: 12, cursor: 'default' }}
                  />
                </div>
              },
            },
          ]}
        />

        <ShowCoachingMarks id='buildNexus' target='#btn-nexus' />
      </div>

      <div className='contain-horiz'>

        <div className='half' style={{ minWidth: 540 }}>
          <CargoGrid
            cargo={this.state.sumCargo}
            linkedFC={visibleFC}
            whereToBuy={whereToBuy}
            minWidthNeed={55}
            commodityNeeds={commodityNeeds}
            ships={ships}
          />
          <br />
          <CargoRemaining sumTotal={cargoRemaining} label='Remaining cargo' />
          <CargoRemaining sumTotal={fcRemaining} label='Fleet Carrier deficit' />
          <br />
        </div>

        <div className='half'>
          {this.renderActiveProjectsAndFCs()}
        </div>

      </div>

      {fcEditMarketId && <FleetCarrier
        marketId={fcEditMarketId}
        onClose={cargoUpdated => {
          // use updated cargo?
          const { linkedFC } = this.state;
          if (cargoUpdated) {
            const fc = linkedFC.find(fc => fc.marketId.toString() === fcEditMarketId);
            if (fc) { fc.cargo = cargoUpdated; }
          }

          const visibleFC = linkedFC.filter(fc => !this.state.hiddenFC.has(fc.marketId));
          this.setState({
            fcEditMarketId: undefined,
            linkedFC: linkedFC,
            fcCargo: mergeCargo(visibleFC.map(fc => fc.cargo)),
          });
        }}
      />}

      {cmdrEdit && <Modal
        isOpen
        styles={{ main: { border: '1px solid ' + appTheme.palette.themePrimary, } }}
      >
        <ModalCommander onComplete={() => this.setState({ cmdrEdit: false })} preAddFC />
      </Modal>}

      {showPreNewFcLoading && this.renderPreNewFcLoading()}
    </div>;
  }

  renderActiveProjectsAndFCs() {
    const { allProjects, projects, linkedFC, hiddenFC, fcCargo, loading, hiddenIDs, originalHiddenIDs } = this.state;

    const mapBySystem = allProjects.reduce((map, p) => {
      if (!map[p.systemName]) { map[p.systemName] = []; }
      map[p.systemName] = [...map[p.systemName], p];
      return map;
    }, {} as Record<string, Project[]>);

    const systemRows = [];
    let first = true;
    const sortedSystems = Object.keys(mapBySystem).sort();
    for (const systemName of sortedSystems) {
      const isFcLoading = systemName === '';
      if (!first) {
        systemRows.push(<div key={`${systemName}-d`} style={{ marginTop: 8, marginBottom: 12, borderTop: `1px dashed ${appTheme.palette.themeTertiary}` }} />);
      }
      first = false;

      let checkCount = 0;
      const projCount = mapBySystem[systemName].length;

      const systemProjectRows = mapBySystem[systemName].map(p => {
        const isPrep = p.buildType === fc_loading;
        const sumNeed = sumCargos(p.commodities);
        const approxProgress = isPrep ? 0 : p.maxNeed - sumNeed;
        const countReadyOnFCs = getCargoCountOnHand(p.commodities, fcCargo);
        const maxNeed = isPrep ? sumNeed : p.maxNeed;
        const percent = p.maxNeed === 0 ? 0 : 100 / maxNeed * (isPrep ? countReadyOnFCs : approxProgress);

        const checkProj = !hiddenIDs.has(p.buildId);
        if (checkProj) { checkCount++; }

        const projSum = sumCargo(p.commodities);
        return <div key={p.buildId} style={{ marginLeft: 32, marginTop: 4, color: checkProj ? undefined : 'grey' }}>

          <Stack horizontal verticalAlign='baseline' style={{ position: 'relative', float: 'right', marginLeft: 8, marginTop: 4 }}>
            <HaulSize haul={projSum} size={1} dim={!checkProj} />
            <span style={{ position: 'relative', marginLeft: 4, top: -1, fontSize: 12 }}>{projSum.toLocaleString()}</span>
          </Stack>

          <Stack className='project-link' horizontal tokens={{ childrenGap: 4 }} verticalAlign='center'>

            <Checkbox checked={checkProj} onChange={(e, c) => {
              // add or remove an entry
              const { hiddenIDs } = this.state;
              if (c) {
                hiddenIDs.delete(p.buildId);
              } else {
                hiddenIDs.add(p.buildId);
              }
              const projects = allProjects.filter(p => !hiddenIDs.has(p.buildId));
              const sumCargo = mergeCargo(projects.map(p => p.commodities));
              const commodityNeeds = this.generateCommodityNeeds(sumCargo, projects);
              this.setState({ hiddenIDs, projects, sumCargo, commodityNeeds });
            }} />

            <ProjectLink proj={p} noSys greyIncomplete={!checkProj} incompleteLinkColor={appTheme.palette.themeTertiary} noType={p.buildType === fc_loading} />

            {p.discordLink && <Icon
              className='icon-btn'
              iconName='OfficeChatSolid'
              title='Open Discord link'
              onClick={() => openDiscordLink(p.discordLink)}
            />}
            {p.buildId === App.cmdrSettings?.primaryBuildId && <Icon iconName='SingleBookmarkSolid' title='This is your current primary project' />}
          </Stack>

          <Stack className='project-link' horizontal tokens={{ childrenGap: 4 }} verticalAlign='center'>
            <ChartGeneralProgress maxNeed={maxNeed} progress={approxProgress} readyOnFC={countReadyOnFCs} minimal />
            <Label style={{ marginBottom: 2, color: checkProj ? undefined : 'grey' }}>&nbsp;&nbsp;{percent.toFixed(0)}%</Label>
          </Stack>

        </div>;
      });

      const checkSys = checkCount === projCount;

      const rowSys = <div key={systemName} style={{ marginTop: 12 }}>
        <Stack horizontal tokens={{ childrenGap: 4 }} verticalAlign='center' style={{ fontWeight: 'bold', marginBottom: 8, color: checkSys ? undefined : 'grey' }} >

          <Checkbox checked={checkSys} indeterminate={checkCount > 0 && checkCount < projCount} onChange={(e, c) => {
            // add or remove an each project
            const { hiddenIDs } = this.state;
            for (const p of mapBySystem[systemName]) {
              if (c) {
                hiddenIDs.delete(p.buildId);
              } else {
                hiddenIDs.add(p.buildId);
              }
            }
            const projects = allProjects.filter(p => !hiddenIDs.has(p.buildId));
            const sumCargo = mergeCargo(projects.map(p => p.commodities));
            const commodityNeeds = this.generateCommodityNeeds(sumCargo, projects);
            this.setState({ hiddenIDs, projects, sumCargo, commodityNeeds });
          }} />

          {isFcLoading && <span>FC Loading:</span>}
          {!isFcLoading && <>
            <Link href={`#sys=${encodeURIComponent(systemName)}`} style={{ color: checkSys ? undefined : appTheme.palette.themeTertiary }}>{systemName}</Link>
            <CopyButton text={systemName} />
          </>}
          <span>{mapBySystem[systemName].length} projects</span>
        </Stack>

        {systemProjectRows}
      </div>;
      systemRows.push(rowSys);
    }

    const cmdrLinkedFC: KnownFC[] = [];
    const projectLinkedFC: KnownFC[] = [];

    const cmdrLinkedFCs = store.cmdrLinkedFCs;

    for (let fc of linkedFC) {
      let projLinked = allProjects.some(p => p.linkedFC.map(_ => _.marketId).includes(fc.marketId));
      if (projLinked) {
        projectLinkedFC.push(fc);
      } else if (!cmdrLinkedFCs) {
        cmdrLinkedFC.push(fc);
      }
      if (fc.marketId in cmdrLinkedFCs) {
        cmdrLinkedFC.push(fc);
      }
    }

    const hiddenIDsChanged = originalHiddenIDs !== Array.from(hiddenIDs).sort().join();

    return <>
      <h3 className={cn.h3}>
        {systemRows.length} Systems: {allProjects.length} active, {projects.length} chosen projects&nbsp;

        {hiddenIDsChanged && <ActionButton
          className={cn.bBox2}
          iconProps={{ iconName: 'Save' }}
          text='Save'
          title='Save changes to chosen projects'
          onClick={() => this.saveHiddenIDs()}
        />}
      </h3>
      <div style={{ fontSize: 14 }}>
        {systemRows}
      </div>

      <div className='linked-fc' style={{ marginTop: 20, fontSize: 14 }}>
        <h3 className={cn.h3} style={{}}>
          {linkedFC.length} Fleet Carriers:
        </h3>
        <div style={{ color: appTheme.palette.themeTertiary, marginTop: -4, marginBottom: 4 }}>Note: FCs must be linked to a project to update automatically</div>

        {!loading && !linkedFC.length && <span className='hint' style={{ color: appTheme.palette.neutralTertiaryAlt }}>None</span>}
        {!loading && linkedFC.length && linkedFC.map(fc => <Stack key={`@${fc.marketId}`} horizontal tokens={{ childrenGap: 4 }} style={{ marginBottom: 4 }}>
          <Checkbox checked={!hiddenFC.has(fc.marketId)} onChange={(e, c) => {
            setTimeout(() => {
              const { hiddenFC } = this.state;
              if (c) {
                hiddenFC.delete(fc.marketId);
              } else {
                hiddenFC.add(fc.marketId);
              }

              const visibleFC = linkedFC.filter(fc => !this.state.hiddenFC.has(fc.marketId));
              const fcCargoNew = mergeCargo(visibleFC.map(fc => fc.cargo));
              this.setState({ hiddenFC, fcCargo: fcCargoNew });
              store.viewAllHiddenFC = Array.from(hiddenFC);
            }, 10);
          }} />

          <Icon iconName='Contact' style={{ color: fc.marketId in cmdrLinkedFCs ? appTheme.palette.accent : 'grey', fontWeight: '' }} title='Commander linked FC' />

          <Icon iconName='Manufacturing' style={{ color: projectLinkedFC.includes(fc) ? appTheme.palette.accent : 'grey' }} title='Project linked FC' />

          <span className={`removable ${cn.removable}`}>
            {fcFullName(fc.name, fc.displayName)}
            &nbsp;
            <Icon
              className={`btn ${cn.btn}`}
              iconName='Edit'
              title={`Edit FC: ${fc.displayName} (${fc.name})`}
              style={{ color: appTheme.palette.themePrimary }}
              onClick={() => {
                this.setState({ fcEditMarketId: fc.marketId.toString() });
              }}
            />
          </span>
        </Stack>)}

        <div style={{ marginTop: 10, marginLeft: 30, color: appTheme.palette.themeSecondary }}>
          <Stack horizontal verticalAlign='center'>
            <Icon iconName='Contact' style={{ fontWeight: '' }} />
            <span>&nbsp;Commander linked FC</span>
            <ActionButton
              iconProps={{ iconName: 'Add' }}
              text='Add'
              title='Link a new Fleet Carrier to your Commander'
              style={{
                marginLeft: 10,
                padding: 0,
                height: 22,
                backgroundColor: appTheme.palette.themeLighter,
              }}
              onClick={() => {
                this.setState({ cmdrEdit: true });
              }}
            />
          </Stack>

          <div>
            <Icon iconName='Manufacturing' style={{}} />
            <span>&nbsp;Project linked FC. Click projects above to link.</span>
          </div>
        </div>
      </div>
    </>;
  }

  renderPreNewFcLoading() {
    const { newFcLoadingName } = this.state;

    return <Dialog
      hidden={false}
      dialogContentProps={{ title: 'New FC Loading Project?' }}
      styles={{ main: { border: '1px solid ' + appTheme.palette.themePrimary, } }}
      minWidth={650}
    >
      <div style={{ marginTop: -10 }}>
        <div style={{ color: appTheme.palette.themeSecondary }}>
          Loading projects are lighter weight, for collecting supplies on Fleet Carriers and tracking cargo needs for multiple builds.
        </div>

        <div style={{ color: appTheme.palette.yellowDark, fontSize: 10, margin: '10px 0' }}>
          <Icon className='icon-inline' iconName='Warning' />
          &nbsp;Experimental feature - these may be hidden or unsupported in older versions of client apps
        </div>

        <TextField
          label='Project Name:'
          value={newFcLoadingName}
          styles={{ fieldGroup: { width: 400 } }}
          onChange={(_, v) => this.setState({ newFcLoadingName: v })}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter') { this.createLoadingFC(newFcLoadingName?.trim()!); }
            if (ev.key === 'Escape') { this.setState({ showPreNewFcLoading: false }); }
          }}
        />
      </div>
      <DialogFooter>
        <DefaultButton text='Create' iconProps={{ iconName: 'CheckMark' }} disabled={!newFcLoadingName?.trim()} onClick={() => { this.createLoadingFC(newFcLoadingName?.trim()!); }} />
        <PrimaryButton text='Cancel' iconProps={{ iconName: 'Cancel' }} onClick={() => this.setState({ showPreNewFcLoading: false })} />
      </DialogFooter>
    </Dialog>;
  }

  createLoadingFC = async (buildName: string) => {
    api.project.createFCLoading(buildName).then(newProject => {
      window.location.assign(`/#build=${newProject.buildId}`);
    });
  };

}

